import { useEffect, useMemo, useState } from 'react';
import type { PaletteEntry } from '../model/format.ts';
import { linkCase, onTerrainStylesChanged, styleChoiceFor, styleData, styleOverride, terrainCellInfo, type CaseState, type TerrainCellInfo } from '../terrain/index.ts';
import { isPartialCase, parseCase, type CaseLayer } from '../terrain/cases.ts';
import { FILL, NONE, type TerrainStyleData } from '../terrain/types.ts';
import { FULL } from '../terrain/shapes.ts';
import { ChoiceList, ChoiceThumb, SpritePicker, TT_CSS, spriteTable } from '../terrain/ttShared.ts';
import { CaseDiagram } from '../terrain/CasesTab.ts';
import { customTileOf } from '../data/customTiles.ts';
import { apply as applyX } from '../model/xform.ts';
import { draftContainer, getContainer, openTileset, useEditor, type TileEditRequest } from './store.ts';
import { ensureLayer } from './actions.ts';
import { bodyXform } from './scene.ts';
import { Icon } from './widgets.tsx';
import { missingCases, type MissingCase } from './pixel/session.ts';

type Pick = { layer: CaseLayer; add: boolean };
/** link the case (inside corners: this shape of air around them only), the general case, or the case with exactly these neighbours */
type Scope = TileEditRequest['scope'];

/** Fix tiles tool: what the terrain shows in the tapped cell and which sprites its dirt and grass cases use. */
export function CaseFixer() {
  const doc = useEditor((s) => s.doc);
  const fc = useEditor((s) => s.fixCell);
  const set = useEditor((s) => s.set);
  const [ver, setVer] = useState(0);
  const [picker, setPicker] = useState<Pick | null>(null);
  const [status, setStatus] = useState('');
  const [scopeSel, setScope] = useState<Partial<Record<CaseLayer, Scope>>>({});
  useEffect(() => onTerrainStylesChanged(() => setVer((v) => v + 1)), []);
  useEffect(() => {
    setStatus('');
    setScope({});
  }, [fc?.x, fc?.y]);
  const cont = doc && fc ? getContainer(doc, fc.container) : null;
  const info = useMemo(() => (cont && fc ? terrainCellInfo(cont, fc.x, fc.y) : null), [cont, fc, ver]);
  const viewContainer = useEditor((s) => s.container);
  const missing = useMemo(() => (doc ? missingCases(doc, viewContainer) : []), [doc, viewContainer, ver]);
  const [showMissing, setShowMissing] = useState(false);

  if (!doc) return null;
  if (!fc && !missing.length)
    return (
      <div className="case-fixer hint-only">
        Tap a terrain cell to see and change its dirt and grass tiles.
      </div>
    );
  if (!fc || showMissing)
    return (
      <div className="case-fixer">
        <style>{TT_CSS}</style>
        <MissingList container={viewContainer} missing={missing} onClose={fc ? () => setShowMissing(false) : undefined} />
      </div>
    );
  const close = () => set({ fixCell: null });
  const d = info ? styleData(info.styleId) : null;
  if (!info || !d)
    return (
      <div className="case-fixer">
        <div className="cf-head">
          <b>Cell {fc.x}, {fc.y}</b>
          <button className="icon-btn" onClick={close}><Icon name="close" size={16} /></button>
        </div>
        <p className="cf-note">
          {cont?.terrains.length
            ? 'No terrain shape covers this cell. Tap a cell of a terrain shape.'
            : `${fc.container === 'level' ? 'This level' : 'This platform'} has no terrain shapes: its tiles are hand-placed (edit them with the tile brush). Shapes drawn with the polygon tool (P) get their tiles from the tile cases.`}
        </p>
      </div>
    );
  const ov = styleOverride(info.styleId) ?? {};
  const table = spriteTable(d, ov);

  // ground tiles start on their exact neighbours; under the surface there is no case to link at all
  const scopeOf = (layer: CaseLayer): Scope => {
    const cs = info[layer];
    if (scopeSel[layer]) return cs.buried && scopeSel[layer] === 'exact' ? 'neighbours' : scopeSel[layer]!;
    if (cs.viaTone) return 'light';
    if (cs.buried) return 'neighbours';
    return layer === 'dirt' || cs.viaExact ? 'neighbours' : 'exact';
  };
  const scope = { dirt: scopeOf('dirt'), grass: scopeOf('grass') };
  const draw = (layer: CaseLayer, mode: TileEditRequest['mode']) => set({ tileEdit: { container: fc.container, x: fc.x, y: fc.y, layer, scope: scope[layer], mode } });
  const target = (layer: CaseLayer) => {
    const cs = info[layer];
    if (scope[layer] === 'general' && cs.general) return { key: cs.general.key, linked: cs.general.linked };
    // a first exact-neighbours link starts from what the cell shows now
    if (scope[layer] === 'light' && cs.tone) return { key: cs.tone.key, linked: cs.tone.linked ?? cs.exact?.linked ?? cs.caseLinked };
    if (scope[layer] === 'neighbours' && cs.exact) return { key: cs.exact.key, linked: cs.exact.linked ?? cs.caseLinked };
    return { key: cs.key, linked: cs.caseLinked };
  };
  const link = (layer: CaseLayer, arr: number[] | null) => {
    const key = target(layer).key;
    if (!key) return;
    setStatus('saving…');
    linkCase(info.styleId, layer, key, arr).then(
      () => setStatus('saved'),
      () => setStatus('saved on this device'),
    );
  };
  const onPick = (pe: PaletteEntry, flags: number) => {
    if (!picker) return;
    const c = styleChoiceFor(info.styleId, pe, flags);
    choose(picker, c);
  };
  const choose = (p: Pick, c: number) => {
    setPicker(null);
    const cur = target(p.layer).linked;
    link(p.layer, p.add && cur ? [...cur, c, 1] : [c, 1]);
  };
  const addGrassLayer = () =>
    useEditor.getState().change('Add grass layer', (dd) => {
      const c = draftContainer(dd, fc.container);
      const id = ensureLayer(c, 'foreground', fc.container !== 'level');
      for (const t of c.terrains) if (!t.targets.foreground) t.targets.foreground = id;
    });

  return (
    <div className="case-fixer">
      <style>{TT_CSS}</style>
      <div className="cf-head">
        <b>Cell {fc.x}, {fc.y}</b>
        <span className="cf-dim">{ov.name ?? d.name}</span>
        <span className="cf-grow" />
        {status && <span className="cf-status">{status}</span>}
        {missing.length > 0 && (
          <button className="chip cf-missing-chip" title="Tile cases with no tile in this level, to draw them one after another" onClick={() => setShowMissing(true)}>
            Missing ({missing.length})
          </button>
        )}
        <button className="chip" title="Every dirt and grass case of this style, including all slope combinations, with drawing templates for the missing ones" onClick={() => openTileset(info.styleId)}>
          <Icon name="cases" size={14} /> All cases
        </button>
        <button className="icon-btn" title="Close" onClick={close}><Icon name="close" size={16} /></button>
      </div>
      {(['dirt', 'grass'] as const).map((layer) => (
        <LayerRow
          key={layer}
          layer={layer}
          info={info}
          d={d}
          table={table}
          scope={scope[layer]}
          onScope={(v) => setScope({ ...scopeSel, [layer]: v })}
          onLink={(a) => link(layer, a)}
          onPick={(add) => setPicker({ layer, add })}
          onDraw={(mode) => draw(layer, mode)}
          onAddLayer={layer === 'grass' ? addGrassLayer : undefined}
        />
      ))}
      <p className="cf-note">
        Changes apply to every cell of this style with the same case, in all levels ("Only these neighbours": only the same neighbours; "Light and dark": those neighbours light or dark the same way). Every case of the style: All cases (Tileset tool → Tile cases).
      </p>
      {picker && (
        <div className="cf-picker">
          <SpritePicker
            d={d}
            table={table}
            variant={info.variant}
            allowFill={picker.layer === 'dirt'}
            allowNone={picker.layer === 'grass'}
            onPick={onPick}
            onPickSpecial={(c) => choose(picker, c)}
            onClose={() => setPicker(null)}
          />
        </div>
      )}
    </div>
  );
}

function sourceText(layer: CaseLayer, info: TerrainCellInfo, cs: CaseState): string {
  if (cs.viaTone) return `Linked by hand for cells with exactly these neighbours where the light and dark match${cs.viaMirror ? ' (mirror image of a linked pattern)' : ''}. A band tile counts as light, even where its edge pixels go dark; plain fill counts as dark.`;
  if (layer === 'dirt' && !cs.key) return 'Deep inside: plain fill (set in Tileset tool → Fill). Cells up to 2 tiles from the surface can get a tile for their exact neighbours.';
  if (cs.buried && !cs.viaExact)
    return info.nb.every((s) => s === FULL)
      ? 'Under the surface, solid all round: plain fill. Pick a tile here to use it wherever the 5×5 around a cell looks exactly like this (e.g. to thicken the band under a slope).'
      : 'Under the surface: no outline faces the air here, so it uses the learned pieces. Pick a tile here to use it wherever the 8 neighbours are exactly these (e.g. below the bottom of a V between slopes).';
  if (layer === 'grass') {
    if (!info.hasForeground) return 'This shape has no grass layer.';
    if (!cs.key) return 'Nothing here faces the air: no grass.';
  }
  const split = layer === 'grass' && isPartialCase(cs.key);
  if (split && !cs.linked && parseCase(cs.key)?.grass?.halves)
    return 'Learned end pieces (not linked). A grassy run beside this cell ends here, its grass comes in on the green half of the line: a sprite picked here is only used for run ends like this.';
  const src = cs.viaExact
    ? `Linked by hand for cells with exactly these neighbours${cs.viaMirror ? ' (mirror image of a linked neighbourhood)' : ''}; other cells of this case keep the case's tiles`
    : cs.viaFallback
    ? 'Nothing linked for this mix of grassy and bare lines yet: uses the tiles linked to the case its grassy part makes'
    : cs.linked
      ? `Linked by hand${split ? ' for this mix of grassy and bare lines' : ''}${cs.viaGeneral ? ' for this inside corner with any slopes around it' : cs.general ? ' for this corner shape' : ''}${cs.viaMirror ? ' (mirror image of a linked case)' : ''}`
      : `Learned pieces (not linked)${split ? '. This mix of grassy and bare lines (grey: no grass) is a case of its own: a sprite picked here is only used where the grass is split like this' : ''}`;
  if (layer === 'grass' && info.edgeMode === 'none') return src + '. Grass is switched off here (shape setting None or erased with the grass brush).';
  if (layer === 'grass' && info.edgeMode === 'full' && cs.linked?.some((c, i) => i % 2 === 0 && c === NONE)) return src + '. Full grass: "nothing" uses the learned pieces here.';
  return src + '.';
}

function LayerRow({ layer, info, d, table, scope, onScope, onLink, onPick, onDraw, onAddLayer }: { layer: CaseLayer; info: TerrainCellInfo; d: TerrainStyleData; table: PaletteEntry[]; scope: Scope; onScope: (s: Scope) => void; onLink: (arr: number[] | null) => void; onPick: (add: boolean) => void; onDraw: (mode: TileEditRequest['mode']) => void; onAddLayer?: () => void }) {
  const cs = info[layer];
  const current = layer === 'dirt' ? info.level : info.fg;
  const custom = current >= 0 && !!customTileOf(table[current >> 3]);
  const can = !!cs.key && (layer === 'dirt' || info.hasForeground);
  const general = scope === 'general' && !!cs.general;
  const near = scope === 'neighbours' && !!cs.exact;
  const lit = scope === 'light' && !!cs.tone;
  // the list being edited: the general case's own link, the light/dark pattern's, the exact neighbours'
  // (until it has one: the case's), or what this case uses (its own or inherited)
  const linked = general ? cs.general!.linked : lit ? (cs.tone!.linked ?? cs.exact?.linked ?? cs.caseLinked) : near ? (cs.exact!.linked ?? cs.caseLinked) : cs.caseLinked;
  const ownLink = general ? !!cs.general!.linked : lit ? !!cs.tone!.linked : near ? !!cs.exact!.linked : !!cs.caseLinked && !cs.viaGeneral && !cs.viaFallback;
  const split = layer === 'grass' && isPartialCase(cs.key);
  const scopeChip = (v: Scope, label: string, title: string) => (
    <button className={`chip ${scope === v || (v === 'exact' && scope !== 'general' && scope !== 'neighbours' && scope !== 'light') ? 'active' : ''}`} title={title} onClick={() => onScope(v)}>
      {label}
    </button>
  );
  const lightChip = scopeChip('light', 'Light and dark', 'Link only cells with exactly these neighbours, and only where each of them is light or dark the same way. A band tile counts as light even where its edge pixels go dark; plain fill counts as dark. Wins over Only these neighbours.');
  return (
    <div className="cf-row">
      <div className="cf-side">
        {cs.key ? <CaseDiagram layer={layer} caseKey={lit && cs.tone ? cs.tone.key : near && cs.exact ? cs.exact.key : cs.key} own={info.own} nb={info.nb} size={66} /> : <div className="cf-nocase" />}
        <span className="cf-dim">{layer === 'dirt' ? 'Dirt' : 'Grass'}</span>
      </div>
      <div className="cf-main">
        <div className="cf-now">
          <span className="cf-dim">Now</span>
          <ChoiceThumb d={d} table={table} choice={current} variant={info.variant} size={36} />
          <span className="cf-src">{sourceText(layer, info, cs)}</span>
        </div>
        {layer === 'grass' && !info.hasForeground && onAddLayer && (
          <button className="chip" onClick={onAddLayer}><Icon name="plus" size={14} /> Add grass layer</button>
        )}
        {can && cs.exact && cs.buried && (
          <div className="cf-scope">
            <span className="cf-dim">Applies to</span>
            {scopeChip('neighbours', 'Only these neighbours', info.nb.every((s) => s === FULL) ? 'Cells whose 5×5 surroundings are exactly these shapes' : 'Cells with exactly these 8 neighbouring shapes')}
            {cs.tone && lightChip}
          </div>
        )}
        {can && cs.exact && !cs.buried && (
          <div className="cf-scope">
            <span className="cf-dim">Applies to</span>
            {cs.general
              ? scopeChip('exact', 'This corner shape', 'Link only cells whose corner has exactly these slopes around it')
              : scopeChip('exact', 'This case', 'Link every cell with this outline facing the air, whatever its neighbours are')}
            {cs.general && scopeChip('general', 'Any corner shape', 'Link this inside corner whatever slopes meet there (corner shapes linked on their own keep their tiles)')}
            {scopeChip('neighbours', 'Only these neighbours', 'Link only cells with exactly these 8 neighbouring shapes (slopes, walls and gaps around it). Wins over the case; other cells of the case keep its tiles')}
            {cs.tone && lightChip}
          </div>
        )}
        {can && linked && (
          <ChoiceList d={d} table={table} variant={info.variant} arr={linked} learned={cs.learned} onChange={(a) => onLink(a.length ? a : null)} onAdd={() => onPick(true)} />
        )}
        {can && !linked && cs.learned.length > 0 && (
          <div className="cf-learned">
            <span className="cf-dim">Learned</span>
            {cs.learned.filter((_, i) => i % 2 === 0).slice(0, 5).map((c, i) => <ChoiceThumb key={i} d={d} table={table} choice={c} variant={info.variant} size={30} />)}
          </div>
        )}
        {can && (
          <div className="cf-actions">
            <button className="chip active" onClick={() => onPick(false)}>Pick sprite…</button>
            <button className="chip" onClick={() => onDraw('new')} title="Draw a tile for this case in the pixel editor (starts from the tile shown now)"><Icon name="pencil" size={14} /> Draw new tile</button>
            {current >= 0 &&
              (custom ? (
                <button className="chip" onClick={() => onDraw('edit')} title="Change this custom tile everywhere it is used"><Icon name="pixel" size={14} /> Edit tile</button>
              ) : (
                <button className="chip" onClick={() => onDraw('copy')} title="Edit a copy of this tile and use it instead (the tileset art is never changed)"><Icon name="pixel" size={14} /> Edit a copy</button>
              ))}
            {layer === 'grass' && <button className="chip" onClick={() => onLink([NONE, 1])}>No grass</button>}
            {layer === 'dirt' && <button className="chip" onClick={() => onLink([FILL, 1])} title="use the interior fill sprites">Plain fill</button>}
            {!linked && cs.learned.length > 0 && <button className="chip" onClick={() => onLink([...cs.learned])} title="link the learned pieces to this case (keeps them when the rules change)">Use learned</button>}
            {ownLink &&
              (lit ? (
                <button className="chip" onClick={() => onLink(null)} title="unlink this light and dark pattern: the cell uses its neighbours' tiles again">Use the neighbours' tiles</button>
              ) : near ? (
                cs.buried ? (
                  <button className="chip" onClick={() => onLink(null)} title="unlink these neighbours: plain fill / learned pieces again">Back to default</button>
                ) : (
                  <button className="chip" onClick={() => onLink(null)} title="unlink these neighbours: the cell uses the tiles of its case again">Use the case's tiles</button>
                )
              ) : !general && cs.general?.linked ? (
                <button className="chip" onClick={() => onLink(null)} title="unlink this corner shape: it uses the tiles linked for any corner shape again">Use any-corner tiles</button>
              ) : split ? (
                <button className="chip" onClick={() => onLink(null)} title="unlink this mix of grassy and bare lines: it uses the tiles of the case its grassy part makes again">Back to default</button>
              ) : (
                <button className="chip" onClick={() => onLink(null)} title="unlink: the learned pieces are used again">Back to learned</button>
              ))}
          </div>
        )}
      </div>
    </div>
  );
}

/** Tile cases this level shows no tile for, most cells first: jump to one, or draw them in a row. */
function MissingList({ container, missing, onClose }: { container: string; missing: MissingCase[]; onClose?: () => void }) {
  const set = useEditor((s) => s.set);
  const shown = missing.slice(0, 40);
  const cellOf = (m: MissingCase) => ({ container, x: m.cells[0][0], y: m.cells[0][1], layer: m.layer });
  const go = (m: MissingCase) => {
    const s = useEditor.getState();
    const c = getContainer(s.doc!, container);
    const [x, y] = c.body ? applyX(bodyXform(c.body), m.cells[0][0] + 0.5, m.cells[0][1] + 0.5) : [m.cells[0][0] + 0.5, m.cells[0][1] + 0.5];
    set({ fixCell: { container, x: m.cells[0][0], y: m.cells[0][1] }, camera: { ...s.camera, x, y } });
    onClose?.();
  };
  const drawFrom = (i: number) => {
    const [first, ...rest] = shown.slice(i).map(cellOf);
    set({ fixCell: { container, x: first.x, y: first.y }, tileEdit: { ...first, scope: 'exact', mode: 'new', queue: rest } });
  };
  const cells = missing.reduce((n, m) => n + m.cells.length, 0);
  return (
    <>
      <div className="cf-head">
        <b>Missing tiles</b>
        <span className="cf-dim">{missing.length} case{missing.length === 1 ? '' : 's'}, {cells} cell{cells === 1 ? '' : 's'}</span>
        <span className="cf-grow" />
        <button className="chip active" onClick={() => drawFrom(0)} title="Draw a tile for each missing case, one after another (Save & next)"><Icon name="pencil" size={14} /> Draw all</button>
        {onClose && <button className="icon-btn" title="Back to the cell" onClick={onClose}><Icon name="close" size={16} /></button>}
      </div>
      <div className="cf-missing">
        {shown.map((m, i) => (
          <div key={`${m.styleId}|${m.layer}|${m.key}`} className="cf-missing-row">
            <CaseDiagram layer={m.layer} caseKey={m.key} own={m.own} nb={m.nb} size={44} />
            <div className="cf-missing-info">
              <span>{m.layer === 'dirt' ? 'Dirt' : 'Grass'}</span>
              <span className="cf-dim">{m.cells.length} cell{m.cells.length === 1 ? '' : 's'} · {styleOverride(m.styleId)?.name ?? styleData(m.styleId)?.name ?? m.styleId}</span>
            </div>
            <button className="chip" onClick={() => go(m)} title="Show the first cell">Go</button>
            <button className="chip" onClick={() => drawFrom(i)} title="Draw this tile, then the ones below it"><Icon name="pencil" size={14} /> Draw</button>
          </div>
        ))}
      </div>
      <p className="cf-note">Tap a terrain cell to see and change its dirt and grass tiles. Red cells in the level have no tile.</p>
    </>
  );
}
